import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Stack,
    Typography,
} from '@mui/material';
import useGetPostedInvoices from '../hooks/useGetPostedInvoices';
import useGetPostedInvoicesIdList, {
    getPreInvoicedIdsRequestParams,
} from '../hooks/useGetPostedInvoicesIdList';
import useLocalStorageState from '../../utils/useLocalStorageState';
import useClearStorageOnLeave from '../../utils/useClearStorageOnLeave';
import { COLORS } from '../../utils/Application_Constants';
import useInvoicingFilters from '../hooks/Invoicing/useFiltersInvoiceManagementFilters';
import FilterDrawer from '../../Shared/Components/FilterDrawer/FilterDrawer';
import SearchInputWithDropdown from '../../Shared/Components/SearchInputWithDropdown';
import {
    managementSearchOptions,
    formatFiltersForManagementApi,
    isBookingSelectable,
} from '../Configurations/invoicingConfig';
import PageContainer from '../Components/PageContainer';
import Tabs from '../../Shared/Components/Tab/Tabs';
import Tab from '../../Shared/Components/Tab/Tab';
import TabPanel from '../../Shared/Components/Tab/TabPanel';
import {
    InvoicingSelectionContextProvider,
    useInvoicingSelectionContext,
} from '../Contexts/InvoicingSelectionContext';
import ListViewTableHeaderRow from '../../Shared/Components/ListViewTable/ListViewTableHeaderRow';
import InvoiceItemContainer from '../Components/Invoicing/InvoiceManagement/InvoiceItemContainer';
import PaginationBar from '../../Shared/Components/PaginationBar';
import { MIN_COLUMN_WIDTH } from '../Components/Invoicing/styles';
import adaptApiInvoiceToUi from '../Components/Invoicing/InvoiceManagement/AdaptApiInvoiceToUi';
import useAdminAuthContext from '../hooks/useAdminAuthContext';
import PreInvoiceConfirmationModal from '../Components/Invoicing/InvoiceManagement/PreInvoiceConfirmationModal';
import BulkPdfViewerModal from '../Components/Invoicing/InvoiceManagement/BulkPdfViewerModal';
import PreInvoiceStatusErrorsBanner from '../Components/Invoicing/InvoiceManagement/PreInvoiceStatusErrorsBanner';
import {
    ShiftSelectionContextProvider,
    useEnhancedShiftSelectionContext,
} from '../Components/Invoicing/ShiftSelectionContext';
import { SelectionStatus } from '../../Shared/Components/ListViewTable/ListViewTableHeaderCheckbox';
import Switch from '../../Shared/Components/Switch';

const DEFAULT_SORT_FIELD = 'invoiceDate';
const DEFAULT_SORT_DIRECTION = 'desc';
const PAGE_NAME = 'invoiceManagement';
const PAGE_SIZE = 20;
const minWidth = MIN_COLUMN_WIDTH;

const TABLE_HEADERS = [
    { label: 'Client', key: 'client', size: 1.7, sx: { minWidth } },
    {
        label: (
            <>
                Customer
                <br />
                Number
            </>
        ),
        title: 'Customer Number',
        key: 'customerNumber',
        size: 0.6,
        sx: { minWidth },
    },
    { label: 'Invoice #', key: 'invoiceNumber', size: 0.8, sx: { minWidth } },
    { label: 'Clinician', key: 'clinician', size: 1.2, sx: { minWidth } },
    { label: 'Booking', key: 'bookingId', size: 0.8, sx: { minWidth }, sortable: false },
    {
        label: (
            <>
                Invoice
                <br />
                Date
            </>
        ),
        title: 'Invoice Date',
        key: 'invoiceDate',
        size: 0.8,
        sx: { minWidth },
    },
    {
        label: (
            <>
                Posted
                <br />
                Date
            </>
        ),
        title: 'Posted Date',
        key: 'postedDate',
        size: 0.8,
        sx: { minWidth },
    },
    { label: 'Status', key: 'status', size: 1, sx: { minWidth: 130 } },
    { label: 'Amount', key: 'amount', size: 0.8, sx: { minWidth } },
    { label: 'Collector', key: 'collector', size: 1, sx: { minWidth } },
    { label: 'Type', key: 'type', size: 0.5, sx: { minWidth } },
    { label: 'Actions', key: 'actions', size: 1, sortable: false },
];

function InvoiceManagementPage() {
    const { isInvoicingSystemAdminUser } = useAdminAuthContext();
    const [disableSelection, setDisableSelection] = useState(!isInvoicingSystemAdminUser);
    const [showDisabledAlert, setShowDisabledAlert] = useState(false);
    const [scrollWidth, setScrollWidth] = useState('100%');
    const tableContentRef = useRef(null);
    const scrollbarRef = useRef(null);
    const {
        selectStatus,
        handleSelectAllBookings,
        selectedBookingsCount,
        handleInitializeNewBookings,
        handleReset: resetSelection,
    } = useInvoicingSelectionContext();

    // Stored separately from list state so clear-on-leave never wipes the toggle itself.
    const { state: rememberSearchAndFilters, setValue: setRememberSearchAndFilters } =
        useLocalStorageState(`${PAGE_NAME}RememberSearchAndFilters`, false);

    useClearStorageOnLeave(
        [
            `${PAGE_NAME}SortField`,
            `${PAGE_NAME}SortDirection`,
            `${PAGE_NAME}Search`,
            `${PAGE_NAME}SelectedTab`,
            `${PAGE_NAME}Filters`,
        ],
        [],
        { enabled: !rememberSearchAndFilters }
    );

    const { state: sortBy, setValue: setSortBy } = useLocalStorageState(
        `${PAGE_NAME}SortField`,
        DEFAULT_SORT_FIELD
    );
    const { state: sortDirection, setValue: setSortDirection } = useLocalStorageState(
        `${PAGE_NAME}SortDirection`,
        DEFAULT_SORT_DIRECTION
    );
    const handleSortChange = useCallback((key, direction) => {
        setSortBy(key);
        setSortDirection(direction);
    }, []);
    const { state: searchData, setValue: setSearch } = useLocalStorageState(`${PAGE_NAME}Search`, {
        searchWord: '',
        searchColumn: '',
    });
    const { state: tabValue, setValue: setTabValue } = useLocalStorageState(
        `${PAGE_NAME}SelectedTab`,
        'pre-invoiced'
    );
    const isPreInvoicedTab = tabValue === 'pre-invoiced';
    const isPreInvoicedTabRef = useRef(isPreInvoicedTab);
    isPreInvoicedTabRef.current = isPreInvoicedTab;

    const {
        filters,
        handleApplyFilter,
        filtersSetup,
        isLoadingFilterConfig,
        appliedFiltersDescription,
    } = useInvoicingFilters(PAGE_NAME, tabValue);

    const [showPreInvoiceConfirmationModal, setShowPreInvoiceConfirmationModal] = useState(false);
    const [preInvoiceAction, setPreInvoiceAction] = useState('invoice');
    const [selectedPdfIds, setSelectedPdfIds] = useState(() => new Set());
    const [pdfViewerState, setPdfViewerState] = useState({ open: false, invoices: [] });

    const [preInvoiceErrorBannerState, setPreInvoiceErrorBannerState] = useState({
        show: false,
        type: '',
        message: '',
        errors: [],
    });

    const handleAbandonSelected = () => {
        setPreInvoiceAction('abandon');
        setShowPreInvoiceConfirmationModal(true);
    };

    const handleInvoiceSelected = () => {
        setPreInvoiceAction('invoice');
        setShowPreInvoiceConfirmationModal(true);
    };

    const [page, setPage] = useState(1);
    const offset = (page - 1) * PAGE_SIZE;
    const handlePageChange = (_, newPage) => setPage(newPage);

    const requestParams = useMemo(() => {
        const formattedFilters = formatFiltersForManagementApi(filters, filtersSetup) || {};

        const searchParams = {};
        if (searchData?.searchWord) {
            searchParams.SearchWord = searchData?.searchWord;
            searchParams.SearchColumn = searchData.searchColumn;
        }

        const sortParams = {};
        if (sortBy) {
            const dir = String(sortDirection || DEFAULT_SORT_DIRECTION).toUpperCase();
            sortParams.orderBy = sortBy;
            sortParams.dir = dir;
        }

        const base = {
            offset,
            pageSize: PAGE_SIZE,
            isPreInvoice: isPreInvoicedTab,
        };

        return { ...base, ...formattedFilters, ...searchParams, ...sortParams };
    }, [offset, tabValue, sortBy, sortDirection, filters, filtersSetup, searchData]);

    const {
        data: invoices,
        isLoading,
        isFetching,
        refetch,
    } = useGetPostedInvoices(requestParams, {
        onSettled: () => {
            // Clear the selected PDF IDs when not on the pre-invoiced tab
            if (!isPreInvoicedTabRef.current) setSelectedPdfIds(new Set());
        },
    });

    const uiInvoices = useMemo(() => {
        const rows = Array.isArray(invoices?.data) ? invoices.data : [];
        return rows
            .map((r) => adaptApiInvoiceToUi(r, { forcePreInvoicedLabel: isPreInvoicedTab }))
            .filter(Boolean);
    }, [invoices, tabValue]);

    const totalCount = invoices?.totalCount ?? 0;
    const totalPages = totalCount ? Math.max(1, Math.ceil(totalCount / PAGE_SIZE)) : 1;

    const { isFetching: isFetchingIds, refetch: fetchIds } = useGetPostedInvoicesIdList(
        getPreInvoicedIdsRequestParams(requestParams),
        {
            enabled: false,
            onSuccess: ({ data: idsData }) => {
                const invoiceIds = idsData?.[0]?.invoiceIds || [];

                if (invoiceIds.length === 0) return;

                const allBookings = invoiceIds.map((id) => ({ bookingId: id }));
                handleInitializeNewBookings(allBookings);
                setDisableSelection(false);
            },
            onError: () => {
                setDisableSelection(true);
                setShowDisabledAlert(true);
            },
        }
    );

    const fetchAllSelectableIdsAndResetSelection = useCallback(() => {
        setShowDisabledAlert(false);
        resetSelection();
        if (isPreInvoicedTab) {
            const hasNoStatusFilter = !filters?.invoiceStatus?.length;
            const includesPreInvoiced = filters?.invoiceStatus?.includes('PreInvoiced');
            const shouldFetchIds =
                isInvoicingSystemAdminUser && (hasNoStatusFilter || includesPreInvoiced);
            if (shouldFetchIds) fetchIds();
        }
    }, [filters, searchData, tabValue, isInvoicingSystemAdminUser]);

    const handleRefetch = useCallback(() => {
        refetch();
        fetchAllSelectableIdsAndResetSelection();
    }, [refetch, fetchAllSelectableIdsAndResetSelection]);

    // When the user changes filters/search/tab/sort, start from page 1
    useEffect(() => {
        setPage(1);

        fetchAllSelectableIdsAndResetSelection();
    }, [fetchAllSelectableIdsAndResetSelection]);

    const { handleSelectBookingClick, resetAnchor } = useEnhancedShiftSelectionContext({
        disableSelection,
        bookings: uiInvoices,
        filterFn: isBookingSelectable,
    });

    useEffect(() => {
        resetAnchor();
    }, [uiInvoices, resetAnchor]);

    // Sync scrollbars and handle resize
    useEffect(() => {
        const tableContent = tableContentRef.current;
        const scrollbar = scrollbarRef.current;

        if (!tableContent || !scrollbar) return;

        const handleTableScroll = () => {
            scrollbar.scrollLeft = tableContent.scrollLeft;
        };

        const handleScrollbarScroll = () => {
            tableContent.scrollLeft = scrollbar.scrollLeft;
        };

        const handleResize = () => {
            if (tableContent) {
                setScrollWidth(`${tableContent.scrollWidth}px`);
            }
        };

        tableContent.addEventListener('scroll', handleTableScroll);
        scrollbar.addEventListener('scroll', handleScrollbarScroll);
        window.addEventListener('resize', handleResize);

        // Update scroll width when content changes
        if (tableContentRef.current) {
            setScrollWidth(`${tableContentRef.current.scrollWidth}px`);
        }

        return () => {
            tableContent.removeEventListener('scroll', handleTableScroll);
            scrollbar.removeEventListener('scroll', handleScrollbarScroll);
            window.removeEventListener('resize', handleResize);
        };
    }, [uiInvoices]);

    const canInvoice = isPreInvoicedTab && isInvoicingSystemAdminUser && !disableSelection;

    const pdfSelectStatus = useMemo(() => {
        if (uiInvoices.length === 0 || selectedPdfIds.size === 0) {
            return SelectionStatus.NOT_SELECTED;
        }
        return selectedPdfIds.size === uiInvoices.length
            ? SelectionStatus.SELECTED
            : SelectionStatus.INDETERMINATE;
    }, [uiInvoices, selectedPdfIds]);

    const handlePdfSelect = useCallback((bookingId, checked) => {
        setSelectedPdfIds((previousIds) => {
            const nextIds = new Set(previousIds);
            if (checked) {
                nextIds.add(bookingId);
            } else {
                nextIds.delete(bookingId);
            }
            return nextIds;
        });
    }, []);

    const handleSelectAllPdfsOnPage = useCallback(
        (checked) => {
            if (checked) {
                const allBookingIds = uiInvoices.map(({ bookingId }) => bookingId);
                setSelectedPdfIds(new Set(allBookingIds));
            } else {
                setSelectedPdfIds(new Set());
            }
        },
        [uiInvoices]
    );

    const handleViewSelectedPdfs = useCallback(() => {
        const selectedPrintableInvoices = uiInvoices
            ?.filter(({ bookingId }) => selectedPdfIds.has(bookingId))
            ?.map(({ bookingId, invoiceNumber, clientName }) => ({
                bookingId,
                invoiceNumber,
                clientName,
            }));

        if (!selectedPrintableInvoices?.length) return;

        setPdfViewerState({ open: true, invoices: selectedPrintableInvoices });
    }, [uiInvoices, selectedPdfIds]);

    const handleClosePdfViewer = useCallback(() => {
        setPdfViewerState({ open: false, invoices: [] });
    }, []);

    const handleSelectAll = useCallback(() => {
        resetAnchor();
        handleSelectAllBookings();
    }, [resetAnchor, handleSelectAllBookings]);

    let tableSelectStatus;
    let tableOnSelectAll;
    if (isPreInvoicedTab && canInvoice) {
        tableSelectStatus = selectStatus;
        tableOnSelectAll = handleSelectAll;
    } else if (!isPreInvoicedTab) {
        tableSelectStatus = pdfSelectStatus;
        tableOnSelectAll = handleSelectAllPdfsOnPage;
    }

    return (
        <PageContainer>
            <Typography variant="h3" sx={{ fontSize: 22 }}>
                Invoice Management
            </Typography>

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                mt="12px"
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <FilterDrawer
                        key={tabValue}
                        isLoading={isLoadingFilterConfig || isFetching}
                        config={filtersSetup}
                        onChange={handleApplyFilter}
                        storedValues={filters}
                    />
                    <FormControlLabel
                        sx={{
                            '& .MuiFormControlLabel-label': {
                                color: COLORS.LT_MIDNIGHT,
                                fontSize: 14,
                                fontStyle: 'normal',
                                fontWeight: 400,
                                lineHeight: '16.5px',
                            },
                        }}
                        checked={!!rememberSearchAndFilters}
                        onChange={(e) => setRememberSearchAndFilters(e.target.checked)}
                        control={<Switch />}
                        label="Remember my search and filters"
                    />
                </Stack>

                <SearchInputWithDropdown
                    isLoading={isFetching}
                    options={managementSearchOptions}
                    initialSearch={searchData?.searchWord}
                    initialSearchKey={searchData?.searchColumn}
                    onSubmit={({ search, searchKey }) =>
                        setSearch({ searchWord: search, searchColumn: searchKey })
                    }
                />
            </Stack>

            <Typography sx={{ my: 2, fontSize: 12, fontWeight: '400', color: COLORS.LT_MIDNIGHT }}>
                {appliedFiltersDescription}
            </Typography>

            {preInvoiceErrorBannerState.show && (
                <PreInvoiceStatusErrorsBanner
                    type={preInvoiceErrorBannerState.type}
                    message={preInvoiceErrorBannerState.message}
                    errors={preInvoiceErrorBannerState.errors}
                    onClose={() => {
                        setPreInvoiceErrorBannerState({
                            show: false,
                            type: '',
                            message: '',
                            errors: [],
                        });
                    }}
                />
            )}

            <Tabs value={tabValue} onChange={(e, value) => setTabValue(value)}>
                <Tab
                    label={`Pre-Invoiced${
                        isPreInvoicedTab && totalCount !== null && !isFetching
                            ? ` (${totalCount})`
                            : ''
                    }`}
                    index={0}
                    value="pre-invoiced"
                />
                <Tab
                    label={`Invoiced${
                        !isPreInvoicedTab && totalCount !== null && !isFetching
                            ? ` (${totalCount})`
                            : ''
                    }`}
                    index={1}
                    value="invoiced"
                />
            </Tabs>

            <TabPanel>
                {!isLoading && uiInvoices?.length > 0 ? (
                    <Box
                        sx={{ py: '22px', px: '26px', borderRadius: '10px', position: 'relative' }}
                    >
                        {isPreInvoicedTab && (
                            <Alert severity="info" sx={{ mb: '15px', fontSize: '12px' }}>
                                You are viewing pre-invoices. These have not been formally invoiced
                                to the client.
                            </Alert>
                        )}

                        <Box mt={0} mb="15px">
                            <PaginationBar
                                page={page}
                                totalPages={totalPages}
                                handlePageChange={handlePageChange}
                            />
                        </Box>

                        {showDisabledAlert && (
                            <Alert
                                severity="error"
                                sx={{ mb: 2 }}
                                onClose={() => setShowDisabledAlert(false)}
                            >
                                Selection has been disabled due to an error fetching selectable
                                items. Please refresh the page or adjust your filters.
                            </Alert>
                        )}

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            mb="20px"
                        >
                            <Typography color={COLORS.LT_MIDNIGHT}>
                                {isPreInvoicedTab ? 'Pre-Invoices' : 'Invoices'}
                            </Typography>

                            {canInvoice && (
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Button
                                        variant="lt-admin-outlined"
                                        color="error"
                                        size="small"
                                        disabled={selectedBookingsCount === 0}
                                        onClick={handleAbandonSelected}
                                    >
                                        Abandon selected ({selectedBookingsCount})
                                    </Button>
                                    <Button
                                        variant="lt-admin-contained"
                                        color="primary"
                                        size="small"
                                        disabled={selectedBookingsCount === 0}
                                        onClick={handleInvoiceSelected}
                                    >
                                        Invoice selected ({selectedBookingsCount})
                                    </Button>
                                </Stack>
                            )}

                            {!isPreInvoicedTab && (
                                <Button
                                    variant="lt-admin-contained"
                                    color="primary"
                                    size="small"
                                    disabled={!selectedPdfIds.size}
                                    onClick={handleViewSelectedPdfs}
                                >
                                    View PDFs ({selectedPdfIds.size})
                                </Button>
                            )}
                        </Stack>

                        <Box
                            ref={tableContentRef}
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                position: 'relative',
                                // Hide the scrollbar of the actual content
                                '&::-webkit-scrollbar': {
                                    display: 'none',
                                },
                                scrollbarWidth: 'none', // For Firefox
                                msOverflowStyle: 'none', // For IE and Edge
                            }}
                        >
                            <ListViewTableHeaderRow
                                selectStatus={tableSelectStatus}
                                onSelectAll={tableOnSelectAll}
                                selectColumnProps={{
                                    sx: {
                                        justifyContent: 'flex-end',
                                        minWidth: '90px',
                                        pr: '2.5px !important',
                                    },
                                }}
                                columns={TABLE_HEADERS}
                                onSortChange={handleSortChange}
                                sortBy={sortBy}
                                sortDirection={sortDirection}
                                shouldCleanSort
                                sortIconPosition="left"
                            />

                            {!isFetching &&
                                !isFetchingIds &&
                                uiInvoices?.map((invoice) => (
                                    <InvoiceItemContainer
                                        key={invoice.bookingId}
                                        invoice={invoice}
                                        disableSelection={disableSelection}
                                        canReprint={!isPreInvoicedTab && invoice.isLatest}
                                        handleRefetch={handleRefetch}
                                        handleSelectBooking={handleSelectBookingClick}
                                        showPdfSelection={!isPreInvoicedTab}
                                        isPdfSelected={selectedPdfIds.has(invoice.bookingId)}
                                        onPdfSelect={(checked) =>
                                            handlePdfSelect(invoice.bookingId, checked)
                                        }
                                    />
                                ))}

                            {(isFetching || isFetchingIds) && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginBottom: '20px',
                                    }}
                                    aria-label="loading-overlay"
                                >
                                    <CircularProgress size={28} />
                                </Box>
                            )}
                        </Box>

                        {/* Static horizontal scrollbar at the bottom */}
                        <Box
                            ref={scrollbarRef}
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                position: 'sticky',
                                bottom: 0,
                                left: 0,
                                zIndex: 10,
                                backgroundColor: 'white',
                                borderTop: '1px solid #e0e0e0',
                                paddingTop: '2px',
                                paddingBottom: '2px',
                                // Force scrollbar to always be visible
                                '&::-webkit-scrollbar': {
                                    height: '10px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: '#f1f1f1',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: '#888',
                                    borderRadius: '6px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    backgroundColor: '#555',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    height: '1px',
                                    width: scrollWidth,
                                }}
                            />
                        </Box>

                        {showPreInvoiceConfirmationModal && (
                            <PreInvoiceConfirmationModal
                                open
                                onClose={() => setShowPreInvoiceConfirmationModal(false)}
                                action={preInvoiceAction}
                                invoices={uiInvoices}
                                onSuccess={({ message, type, errors }) => {
                                    setPreInvoiceErrorBannerState({
                                        show: true,
                                        type,
                                        message,
                                        errors,
                                    });
                                    handleRefetch();
                                }}
                            />
                        )}

                        <BulkPdfViewerModal
                            open={pdfViewerState.open}
                            onClose={handleClosePdfViewer}
                            invoices={pdfViewerState.invoices}
                        />
                    </Box>
                ) : (
                    <Typography variant="body1" component="p" p={2}>
                        {isLoading ? <CircularProgress /> : 'No results found'}
                    </Typography>
                )}
            </TabPanel>

            {uiInvoices?.length > 0 && (
                <Box mt="15px">
                    <PaginationBar
                        page={page}
                        totalPages={totalPages}
                        handlePageChange={handlePageChange}
                        isLoading={isFetching}
                    />
                </Box>
            )}
        </PageContainer>
    );
}

export default function InvoiceManagementPageWithContext() {
    return (
        <InvoicingSelectionContextProvider bookings={[]} totalCount={[]}>
            <ShiftSelectionContextProvider>
                <InvoiceManagementPage />
            </ShiftSelectionContextProvider>
        </InvoicingSelectionContextProvider>
    );
}
